import { merchantLabel } from './reportedUsageUpload'

export type ConnectionMethodId = 'fleet_email' | 'forward_inbox'

export type ConnectionSettings = {
  fleetEmailEnabled: boolean
  forwardEnabled: boolean
  forwardAddress: string
}

export type ConnectionStatus = {
  text: 'Connected' | 'Finish setup' | 'Not connected'
  color: 'green' | 'yellow' | 'gray'
}

export type ConnectionMethodContent = {
  id: ConnectionMethodId
  title: string
  summary: string
  body: string
  steps: string[]
  addressLabel: string
}

const STORAGE_KEY = 'fleet.reportedUsage.connections'
const USAGE_DOMAIN = 'usage.movewithfleet.com'

export const defaultConnectionSettings = (): ConnectionSettings => ({
  fleetEmailEnabled: true,
  forwardEnabled: false,
  forwardAddress: '',
})

export const isEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

export const fleetUsageAddress = (launchId: string, merchantId: string) => {
  const launch =
    launchId
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .slice(0, 12) || 'program'
  const merchant =
    merchantId
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .slice(0, 32) || 'merchant'
  return `${merchant}.${launch}@${USAGE_DOMAIN}`
}

const membershipSteps = (label: string) => [
  `Export the ${label} corporate membership list, or schedule that export.`,
  'Send the file to the Fleet address.',
  'Include corporate email and annual cost so Fleet can record each membership.',
]

const FLEET_STEPS: Record<string, (label: string) => string[]> = {
  uber: () => [
    'In Uber for Business, open the program that pays for these trips.',
    'Schedule the transaction export and add the Fleet address as a recipient.',
    'Send the CSV that includes rider email, trip id, and transaction amount.',
  ],
  waymo: () => [
    'In the Waymo business account, open scheduled trip reports.',
    'Send the report to the Fleet address.',
    'Include rider business email, trip id, and fare.',
  ],
  ridepanda: () => [
    'Ask Ridepanda to email employer invoices to the Fleet address, or add it as a billing recipient.',
    'Each invoice should include the rider email, invoice number, and amount due.',
    'Fleet records the invoice on this program when it arrives.',
  ],
  lyft: membershipSteps,
  citi_bike: membershipSteps,
  capital_bikeshare: membershipSteps,
}

export const connectionIntro = (merchantId: string) => {
  const label = merchantLabel(merchantId)
  return `Fleet email is on for ${label}. Turn on team inbox if those reports already go to a company address.`
}

export const connectionMethods = (
  merchantId: string
): ConnectionMethodContent[] => {
  const label = merchantLabel(merchantId)
  const fleetSteps =
    FLEET_STEPS[merchantId]?.(label) || [
      `Add the Fleet address where ${label} sends reports.`,
      'Send the CSV or Excel file Fleet already accepts for this merchant.',
      'Fleet files the attachment on this program and matches rows to commuters.',
    ]

  return [
    {
      id: 'fleet_email',
      title: 'Fleet email',
      summary: `${label} emails reports directly to Fleet.`,
      body: `Add this address in the ${label} account, or give it to the person who schedules the export. Fleet saves the attachment on this program and matches each row to a commuter.`,
      steps: fleetSteps,
      addressLabel: 'Add this address',
    },
    {
      id: 'forward_inbox',
      title: 'Team inbox',
      summary: `A company inbox already receives ${label} reports. Forward them to Fleet.`,
      body: `Use this when ${label} already emails someone on your team. Enter that inbox, then forward each new report to the Fleet address. Fleet reads the attachment and keeps it with this merchant.`,
      steps: [
        `Enter the inbox that receives the ${label} reports.`,
        'Forward each new message to the Fleet address, or create a mail rule that does it.',
        'Leave the attachment unchanged. Fleet reads CSV and Excel files.',
      ],
      addressLabel: 'Forward reports here',
    },
  ]
}

const forwardReady = (settings: ConnectionSettings) =>
  settings.forwardEnabled && isEmail(settings.forwardAddress)

const forwardIncomplete = (settings: ConnectionSettings) =>
  settings.forwardEnabled && !isEmail(settings.forwardAddress)

export const connectionStatus = (
  settings: ConnectionSettings
): ConnectionStatus => {
  if (forwardIncomplete(settings)) {
    return { text: 'Finish setup', color: 'yellow' }
  }
  if (settings.fleetEmailEnabled || forwardReady(settings)) {
    return { text: 'Connected', color: 'green' }
  }
  return { text: 'Not connected', color: 'gray' }
}

export const enabledConnectionLabels = (settings: ConnectionSettings) => {
  const labels: string[] = []
  if (settings.fleetEmailEnabled) labels.push('Fleet email')
  if (settings.forwardEnabled) labels.push('Team inbox')
  return labels
}

export const connectionSummary = (settings: ConnectionSettings) => {
  if (forwardIncomplete(settings)) {
    return 'Add the inbox that receives these reports'
  }
  const labels = enabledConnectionLabels(settings)
  if (!labels.length) return 'No connection turned on'
  return labels.join(' · ')
}

const isSettings = (value: unknown): value is ConnectionSettings => {
  if (!value || typeof value !== 'object') return false
  const row = value as ConnectionSettings
  return (
    typeof row.fleetEmailEnabled === 'boolean' &&
    typeof row.forwardEnabled === 'boolean' &&
    typeof row.forwardAddress === 'string'
  )
}

const readStore = (): Record<string, Record<string, ConnectionSettings>> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed as Record<string, Record<string, ConnectionSettings>>
  } catch {
    return {}
  }
}

export const readConnectionSettings = (
  launchId: string,
  merchantId: string
): ConnectionSettings => {
  const row = readStore()[launchId]?.[merchantId]
  return isSettings(row) ? row : defaultConnectionSettings()
}

export const readLaunchConnections = (launchId: string) => {
  const rows = readStore()[launchId] || {}
  return Object.fromEntries(
    Object.entries(rows).filter(([, value]) => isSettings(value))
  ) as Record<string, ConnectionSettings>
}

export const writeConnectionSettings = (
  launchId: string,
  merchantId: string,
  settings: ConnectionSettings
) => {
  try {
    const store = readStore()
    const launch = { ...(store[launchId] || {}), [merchantId]: settings }
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...store, [launchId]: launch })
    )
  } catch {
    return
  }
}
